// libs
import React, { memo, useCallback } from 'react';
import { useStoreIcon, IconName } from 'shared/icons';

// styles
import {
  Container,
  SubTitle,
  AnchorTitle,
  TextTitle,
  AnchorVisitStore,
  TitleWrapper,
  ActionContainer,
  TitleContainer,
  ProductTitle,
  IconWrapper,
} from './styles';

// components
import ShareLink from 'shared/layouts/DetailsPage/components/ShareLink';

// helpers
import { storeAnalyticsDataInLocal } from 'shared/helpers/storage/storeData';
import { trackBrandNameClicked } from 'shared/layouts/DetailsPage/analytics';
import { getEligibleVariantName } from 'shared/components/ExperimentV2/helpers';

// constants
import { BRAND_CLICK_TRACK_LOCATION } from 'shared/helpers/analytics/constants/pdpEvents';
import { SEARCH_BAR_EXPT_NAME, SEARCH_BAR_ELIGIBLE_VARIANT, ICON_SIZE } from 'shared/constants';
import { getStoreUrl } from 'shared/helpers/storeUrl';

const VISIT_STORE = 'Visit Store';

// defs
interface ITitleProps {
  title: string;
  subTitle: string;
  actionUrl?: string;
  showChevron?: boolean;
}

function Title({ title, subTitle, actionUrl, showChevron }: ITitleProps) {
  const ArrowRight = useStoreIcon(IconName.CHEVRON_RIGHT_SMALL);
  const { isEligible, variant: isPdpSearchBarVariant = '' } =
    getEligibleVariantName(SEARCH_BAR_EXPT_NAME);
  const pdpSearchBarEnabled = isEligible && isPdpSearchBarVariant === SEARCH_BAR_ELIGIBLE_VARIANT;

  const navigateTo = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, trackingFn?: () => void) => {
      e.preventDefault();
      trackingFn?.();
      if (actionUrl) {
        window.location.href = getStoreUrl(actionUrl);
      }
    },
    [actionUrl]
  );

  const onBrandTitleClicked = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!actionUrl) return;
      storeAnalyticsDataInLocal({ brandTitleOrAboutUs: 'brandTitle' });
      navigateTo(e, () => trackBrandNameClicked(BRAND_CLICK_TRACK_LOCATION.TITLE_CLICK));
    },
    [actionUrl, navigateTo]
  );

  const BRAND_TITLE = actionUrl ? (
    <AnchorTitle
      href={actionUrl}
      data-test="brand-url"
      onClick={onBrandTitleClicked}
      aria-label={`${title} - ${subTitle}`}
    >
      {title}
    </AnchorTitle>
  ) : (
    <TextTitle data-test="brand-title">{title}</TextTitle>
  );

  return (
    <Container data-test="title">
      <TitleContainer>
        <TitleWrapper data-at="product-name">
          <ProductTitle>
            {BRAND_TITLE}
            {pdpSearchBarEnabled && actionUrl && showChevron && (
              <IconWrapper
                href={actionUrl}
                onClick={(e) =>
                  navigateTo(e, () =>
                    trackBrandNameClicked(BRAND_CLICK_TRACK_LOCATION.VIEW_BRAND_CTA)
                  )
                }
                data-test="title-chevron"
                aria-label={`${VISIT_STORE} - ${title}`}
              >
                <ArrowRight size={ICON_SIZE} />
              </IconWrapper>
            )}
          </ProductTitle>
          <SubTitle data-test="sub-title">{subTitle}</SubTitle>
        </TitleWrapper>
      </TitleContainer>

      {pdpSearchBarEnabled ? (
        <ActionContainer>
          <ShareLink size={ICON_SIZE} />
        </ActionContainer>
      ) : (
        actionUrl && (
          <AnchorVisitStore
            href={actionUrl}
            onClick={(e) =>
              navigateTo(e, () => trackBrandNameClicked(BRAND_CLICK_TRACK_LOCATION.VIEW_BRAND_CTA))
            }
            data-test="visit-store-anchor"
            aria-label={`${VISIT_STORE} - ${title}`}
          >
            {VISIT_STORE}
          </AnchorVisitStore>
        )
      )}
    </Container>
  );
}

export default memo(Title);
